import i18n from "i18next";

/**
 * Interpolates DB template placeholders (#p1..#p5) with the recipient's params.
 * Used as a fallback when the i18n template for a notification id is missing.
 */
export function interpolateNotificationTemplate(template, params) {
  let result = template ?? "";
  // DB templates carry "#pN"; the generated i18n JSON uses "{pN}". The
  // fallback path can receive either, so both forms map to the same value.
  const paramMap = {
    "#p1": params.param1,
    "{p1}": params.param1,
    "#p2": params.param2,
    "{p2}": params.param2,
    "#p3": params.param3,
    "{p3}": params.param3,
    "#p4": params.param4,
    "{p4}": params.param4,
    "#p5": params.param5,
    "{p5}": params.param5,
  };
  for (const [placeholder, value] of Object.entries(paramMap)) {
    if (value != null) {
      // split/join instead of replaceAll — not polyfilled under CRA's browserslist
      result = result.split(placeholder).join(value);
    }
  }
  return result;
}

/**
 * Returns the { title, content, cta } for a notification recipient.
 * Primary source: the `notifications` i18n namespace (templates.{id}.* with
 * {pN} interpolation) so copy follows the app language. Falls back to the
 * raw DB template strings when the i18n key doesn't exist.
 */
export function getInterpolatedNotification(notification, t) {
  const { notifications, param1, param2, param3, param4, param5 } =
    notification;
  const safeNotifications = notifications ?? {
    id: "",
    title: "",
    content: "",
    cta: "",
  };
  const notificationId = String(safeNotifications.id);
  const params = { param1, param2, param3, param4, param5 };
  const tParams = {
    p1: param1 ?? "",
    p2: param2 ?? "",
    p3: param3 ?? "",
    p4: param4 ?? "",
    p5: param5 ?? "",
  };
  // The generated templates use single braces ({pN}); i18next defaults to
  // double. Overridden per call so the other namespaces, which do use {{...}},
  // are unaffected — as is i18next's own loadPath.
  const tOptions = {
    ...tParams,
    interpolation: { prefix: "{", suffix: "}" },
  };

  if (t && i18n.exists(`notifications:templates.${notificationId}.title`)) {
    const cta = i18n.exists(`notifications:templates.${notificationId}.cta`)
      ? t(`templates.${notificationId}.cta`, tOptions)
      : null;
    const cta2 = i18n.exists(`notifications:templates.${notificationId}.cta2`)
      ? t(`templates.${notificationId}.cta2`, tOptions)
      : null;
    return {
      title: t(`templates.${notificationId}.title`, tOptions),
      content: t(`templates.${notificationId}.content`, tOptions),
      cta: cta || null,
      cta2: cta2 || null,
    };
  }

  return {
    title: interpolateNotificationTemplate(safeNotifications.title, params),
    content: interpolateNotificationTemplate(safeNotifications.content, params),
    cta: safeNotifications.cta
      ? interpolateNotificationTemplate(safeNotifications.cta, params)
      : null,
    cta2: null,
  };
}
